import { ref, computed } from 'vue'
import {
  getAvailableTickets,
  getTicketById,
  checkTicketAvailability,
  calculateTicketTotal,
  validateTicketQuantity,
  isTicketAvailable,
  formatSalesPeriod
} from '../services/api/tickets'

export function useTickets() {
  // Estado
  const tickets = ref([])
  const selectedTicket = ref(null)
  const selectedQuantity = ref(1)
  const loading = ref(false)
  const error = ref(null)

  // Computed
  const availableTickets = computed(() => {
    return tickets.value.filter(ticket => isTicketAvailable(ticket))
  })

  const selectedTicketTotal = computed(() => {
    if (!selectedTicket.value) return 0
    return calculateTicketTotal(selectedTicket.value, selectedQuantity.value)
  })

  const isQuantityValid = computed(() => {
    if (!selectedTicket.value) return false
    const validation = validateTicketQuantity(selectedTicket.value, selectedQuantity.value)
    return validation.valid
  })

  const quantityValidationMessage = computed(() => {
    if (!selectedTicket.value) return ''
    const validation = validateTicketQuantity(selectedTicket.value, selectedQuantity.value)
    return validation.message
  })

  // Métodos
  const loadTickets = async (eventSlug) => {
    console.log('🔄 useTickets.loadTickets() called with eventSlug:', eventSlug);
    loading.value = true
    error.value = null

    try {
      console.log('📡 Calling getAvailableTickets API...');
      const data = await getAvailableTickets(eventSlug)
      console.log('✅ Tickets loaded successfully:', data);
      tickets.value = data
      return data
    } catch (err) {
      console.error('❌ Error in loadTickets:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        fullError: err
      });
      error.value = err.response?.data?.message || err.message || 'Erro ao carregar ingressos'
      throw err
    } finally {
      loading.value = false
    }
  }

  const selectTicket = (ticket) => {
    selectedTicket.value = ticket
    selectedQuantity.value = ticket.min_per_order || 1
  }

  const setQuantity = (quantity) => {
    const qty = parseInt(quantity)
    if (isNaN(qty) || qty < 1) {
      selectedQuantity.value = 1
      return
    }
    selectedQuantity.value = qty
  }

  const incrementQuantity = () => {
    if (!selectedTicket.value) return

    const newQty = selectedQuantity.value + 1
    const validation = validateTicketQuantity(selectedTicket.value, newQty)

    if (validation.valid) {
      selectedQuantity.value = newQty
    }
  }

  const decrementQuantity = () => {
    if (!selectedTicket.value) return

    const newQty = selectedQuantity.value - 1
    if (newQty < 1) return

    const validation = validateTicketQuantity(selectedTicket.value, newQty)

    if (validation.valid) {
      selectedQuantity.value = newQty
    }
  }

  const checkAvailability = async (ticketId, quantity) => {
    try {
      const result = await checkTicketAvailability(ticketId, quantity)
      return result
    } catch (err) {
      console.error('Error checking availability:', err)
      throw err
    }
  }

  const getTicketDetails = async (ticketId) => {
    loading.value = true
    error.value = null

    try {
      const ticket = await getTicketById(ticketId)
      return ticket
    } catch (err) {
      error.value = err.message
      console.error('Error loading ticket details:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const getSalesPeriod = (ticket) => {
    return formatSalesPeriod(ticket)
  }

  const clearSelection = () => {
    selectedTicket.value = null
    selectedQuantity.value = 1
  }

  const hasTicketSelected = computed(() => {
    return selectedTicket.value !== null
  })

  const canProceedToCheckout = computed(() => {
    return hasTicketSelected.value && isQuantityValid.value
  })

  return {
    // Estado
    tickets,
    selectedTicket,
    selectedQuantity,
    loading,
    error,

    // Computed
    availableTickets,
    selectedTicketTotal,
    isQuantityValid,
    quantityValidationMessage,
    hasTicketSelected,
    canProceedToCheckout,

    // Métodos
    loadTickets,
    selectTicket,
    setQuantity,
    incrementQuantity,
    decrementQuantity,
    checkAvailability,
    getTicketDetails,
    formatPrice,
    getSalesPeriod,
    clearSelection
  }
}
